import { Building2, Target, Heart, Shield, Globe, Linkedin, Newspaper } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion"
import { useRef, useState, useEffect } from "react"

// Import SVG decorations
import ellipse29 from "@/assets/decorations/Ellipse 29.svg"
import ellipse30 from "@/assets/decorations/Ellipse 30.svg"
import ellipse31 from "@/assets/decorations/Ellipse 31.svg"
import ellipse33 from "@/assets/decorations/Ellipse 33.svg"
import line4 from "@/assets/decorations/Line 4.svg"
import line5 from "@/assets/decorations/Line 5.svg"
import line6 from "@/assets/decorations/Line 6.svg"
import line7 from "@/assets/decorations/Line 7.svg"
import line8 from "@/assets/decorations/Line 8.svg"
import line10 from "@/assets/decorations/Line 10.svg"
import line24 from "@/assets/decorations/Line 24.svg"
import line27 from "@/assets/decorations/Line 27.svg"

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Floating animation for decorations
const floatingAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 3,
    repeat: Infinity,
    repeatType: "reverse" as const,
    ease: "easeInOut"
  }
};

const pulseAnimation = {
  scale: [1, 1.1, 1],
  opacity: [0.7, 1, 0.7],
  transition: {
    duration: 4,
    repeat: Infinity,
    repeatType: "reverse" as const
  }
};

const spinAnimation = {
  rotate: [0, 360],
  transition: {
    duration: 20,
    repeat: Infinity,
    ease: "linear"
  }
};

const values = [
  {
    icon: Target,
    title: "Innovation First",
    description: "Pushing boundaries with cutting-edge solutions and forward-thinking approaches."
  },
  {
    icon: Heart,
    title: "Client Success",
    description: "Dedicated to delivering exceptional value and achieving client objectives."
  },
  {
    icon: Shield,
    title: "Integrity",
    description: "Upholding the highest standards of transparency and ethical conduct."
  },
  {
    icon: Globe,
    title: "Global Impact",
    description: "Creating positive change through sustainable and responsible practices."
  }
];

const leadership = [
  {
    name: "Alfredo Diaz",
    role: "Founder & Managing Partner",
    bio: "My career has been shaped by a deep passion for solving complex challenges at the intersection of finance, technology, and innovation. With a background spanning fintech, venture, and product leadership, I've built and scaled multi-product ecosystems, adviced high-growth startups, and developed solutions that empower businesses and individuals across diverse industries.\n\nAt PSHG, my focus is on identifying and unlocking opportunities in alternative investments, fintech infrastructure, and emerging technologies, particularly in underrepresented markets. I believe in building solutions that are both scalable and impactful, leveraging data-driven insights, strategic partnerships, and cutting-edge technology to drive sustainable growth.\n\nPrior to founding PSHG, I played key roles in architecting fintech platforms, designing AI-powered productivity tools, and expanding marketplace ecosystems across the Bay Area, USA, and LATAM. My work has always been driven by a desire to bridge gaps in financial accessibility, streamline investment opportunities, and create long-term value.\n\nI'm always open to collaboration and new ideas—if our vision aligns, let's connect."
  }
];

export function AboutPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Animation values for decorations
  const topLeftRotate = useTransform(scrollYProgress, [0, 1], [0, 45]);
  const topRightY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const midRightX = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const bottomLeftY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const missionCircleScale = useTransform(scrollYProgress, [0.2, 0.4], [1, 1.2]);
  const valuesLineX = useTransform(scrollYProgress, [0.3, 0.6], [0, -40]);
  const leadershipDecorOpacity = useTransform(scrollYProgress, [0.6, 0.8], [0.2, 0.8]);

  // Parallax mouse movement for background decorations
  const parallaxX = (factor: number) => {
    return mousePosition.x * factor;
  };

  const parallaxY = (factor: number) => {
    return mousePosition.y * factor;
  };

  return (
    <div ref={containerRef} className="space-y-16 py-16 px-0 md:px-0 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-neutral-50 dark:to-neutral-950 opacity-60 pointer-events-none z-0"></div>

      {/* Decorative elements that follow mouse subtly */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
        style={{
          x: parallaxX(-0.02),
          y: parallaxY(-0.02)
        }}
      >
        <motion.img
          src={ellipse30}
          alt=""
          className="absolute top-[10%] left-[8%] w-16 h-16 opacity-10"
          animate={spinAnimation}
        />

        <motion.img
          src={line4}
          alt=""
          className="absolute bottom-[15%] right-[10%] w-20 h-20 opacity-10"
          animate={spinAnimation}
        />
      </motion.div>

      {/* Decorative elements */}
      <motion.img
        src={ellipse30}
        alt=""
        className="absolute top-20 left-6 w-8 h-8 opacity-60 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{ rotate: topLeftRotate }}
      />

      <motion.img
        src={line5}
        alt=""
        className="absolute top-32 right-8 w-6 h-6 opacity-40 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={floatingAnimation}
        transition={{ duration: 0.8, delay: 0.3, opacity: 0.4, scale: 1 }}
        style={{ y: topRightY }}
      />

      <motion.img
        src={line4}
        alt=""
        className="absolute top-1/3 right-12 w-7 h-7 opacity-30 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.3, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{ x: midRightX }}
      />

      <motion.img
        src={ellipse31}
        alt=""
        className="absolute bottom-40 left-8 w-8 h-8 opacity-50 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={pulseAnimation}
        transition={{ duration: 0.8, delay: 0.5, opacity: 0.5, scale: 1 }}
        style={{ y: bottomLeftY }}
      />

      {/* Additional decorations */}
      <motion.img
        src={line6}
        alt=""
        className="absolute top-[45%] left-10 w-5 h-5 opacity-30 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={floatingAnimation}
        transition={{ duration: 0.8, delay: 0.5, opacity: 0.3, scale: 1 }}
      />

      <motion.img
        src={line7}
        alt=""
        className="absolute top-[60%] right-6 w-10 h-10 opacity-20 z-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={pulseAnimation}
        transition={{ duration: 0.8, delay: 0.6, opacity: 0.2, scale: 1 }}
      />

      {/* Hero Section */}
      <div className="px-4 sm:px-6 py-6 md:py-8 relative">
        <motion.img
          src={line8}
          alt=""
          className="absolute -top-4 right-1/4 w-5 h-5 opacity-30"
          initial={{ opacity: 0, scale: 0 }}
          animate={floatingAnimation}
          transition={{ duration: 0.8, delay: 0.6, opacity: 0.3, scale: 1 }}
        />

        <motion.img
          src={line24}
          alt=""
          className="absolute top-32 left-1/4 w-6 h-6 opacity-20"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.2, scale: 1, rotate: 45 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        />

        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <motion.img
              src="/pshg_fullname_transparent.png"
              alt="Polymath Solutions Holding Group, LLC"
              className="h-auto max-w-full w-64 sm:w-72 md:w-80"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            />
          </div>
          <motion.h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 md:mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Shaping the Future of Finance
          </motion.h1>
          <motion.p
            className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Polymath Solutions combines deep financial expertise with technological 
            innovation to create lasting value for our clients and communities.
          </motion.p>
          <motion.div
            className="bg-neutral-100 dark:bg-neutral-800 p-4 sm:p-5 rounded-lg text-left max-w-3xl mx-auto mb-6 relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Decorative background elements */}
            <motion.div
              className="absolute -right-10 -bottom-10 w-32 h-32 rounded-full bg-gradient-to-br from-purple-500/10 to-transparent"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />

            <motion.img
              src={line27}
              alt=""
              className="absolute -bottom-3 -right-3 w-6 h-6 opacity-30"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.3, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            />

            <p className="text-xs sm:text-sm md:text-base text-neutral-700 dark:text-neutral-300 leading-relaxed">
              Polymath Solutions Holding Group is a dynamic, cross-disciplinary enterprise that bridges innovation in investment, software, consultancy, and research. Founded by a forward-thinking entrepreneur who believes that diverse expertise fuels progress, PSHG harnesses advanced technology and financial acumen to create sustainable, transformative solutions. Our mission is to empower businesses and communities by unlocking opportunities at the intersection of finance, technology, and strategic insight—ensuring every challenge is met with both analytical precision and creative innovation.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Mission Section - Full Width */}
      <div className="w-full relative">
        <motion.img
          src={ellipse29}
          alt=""
          className="absolute top-1/2 left-10 transform -translate-y-1/2 w-10 h-10 opacity-20 z-10"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.2, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ scale: missionCircleScale }}
        />

        <motion.img
          src={line10}
          alt=""
          className="absolute top-1/3 right-12 w-7 h-7 opacity-20 z-10"
          initial={{ opacity: 0, scale: 0 }}
          animate={floatingAnimation}
          transition={{ duration: 0.8, delay: 0.4, opacity: 0.2, scale: 1 }}
        />

        {/* Additional decorative elements for mission section */}
        <motion.div
          className="absolute left-0 top-0 w-full h-full overflow-hidden pointer-events-none"
          style={{ opacity: useTransform(scrollYProgress, [0.15, 0.3, 0.45], [0, 0.8, 0]) }}
        >
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border border-purple-500/20"
            animate={spinAnimation}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-cyan-500/10"
            animate={{
              ...spinAnimation,
              transition: {
                ...spinAnimation.transition,
                duration: 15
              }
            }}
          />
        </motion.div>

        <Card variant="filled" size="large" className="rounded-none w-full">
          <CardContent className="py-8 md:py-12">
            <motion.div
              className="max-w-3xl mx-auto text-center space-y-4 md:space-y-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div
                variants={itemVariants}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Building2 className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 mx-auto text-neutral-400" />
              </motion.div>
              <motion.h2
                className="text-xl sm:text-2xl md:text-3xl font-bold"
                variants={itemVariants}
              >
                Our Mission
              </motion.h2>
              <motion.p
                className="text-sm sm:text-base md:text-lg text-neutral-300 px-2 sm:px-0 max-w-xl mx-auto"
                variants={itemVariants}
              >
                To empower organizations and individuals through innovative financial 
                solutions, expert consulting, and impactful community initiatives. 
                We strive to create a more inclusive and sustainable financial future.
              </motion.p>
            </motion.div>
          </CardContent>
        </Card>
      </div>

      {/* Values Section */}
      <div className="px-4 sm:px-6 relative">
        <motion.img
          src={line6}
          alt=""
          className="absolute top-10 left-20 w-6 h-6 opacity-30 z-10"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ x: valuesLineX }}
        />

        <motion.img
          src={line24}
          alt=""
          className="absolute bottom-10 right-16 w-8 h-8 opacity-20 z-10"
          initial={{ opacity: 0, scale: 0 }}
          animate={pulseAnimation}
          transition={{ duration: 0.8, delay: 0.4, opacity: 0.2, scale: 1 }}
        />

        {/* Background pattern */}
        <motion.div
          className="absolute top-1/4 right-0 w-80 h-80 rounded-full bg-gradient-to-tl from-cyan-500/5 to-transparent -z-10"
          style={{
            opacity: useTransform(scrollYProgress, [0.3, 0.4, 0.5], [0, 0.6, 0]),
            scale: useTransform(scrollYProgress, [0.3, 0.5], [0.8, 1.2])
          }}
        />

        <motion.div
          className="text-center space-y-4 mb-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <motion.h2
            className="text-xl sm:text-2xl md:text-3xl font-bold"
            variants={itemVariants}
          >
            Our Values
          </motion.h2>
          <motion.p
            className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400"
            variants={itemVariants}
          >
            The principles that guide everything we do
          </motion.p>
        </motion.div>

        {/* Mobile scrollable view */}
        <div className="sm:hidden overflow-x-auto pb-6 -mx-4 px-4">
          <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                >
                  <Card variant="default" className="w-64 flex-shrink-0 overflow-hidden">
                    {/* Decorative accent */}
                    <div className="h-1 bg-gradient-to-r from-purple-500 to-cyan-500" />
                    <CardContent className="p-4 text-center h-full flex flex-col justify-center space-y-3 relative">
                      {/* Background decoration */}
                      <motion.div
                        className="absolute -right-4 -bottom-4 w-12 h-12 rounded-full bg-cyan-500/10"
                        animate={pulseAnimation}
                      />
                      <Icon className="h-6 w-6 mx-auto text-neutral-600 dark:text-neutral-400" />
                      <h3 className="text-base font-semibold">{value.title}</h3>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Desktop grid view */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((value, index) => {
            const Icon = value.icon
            return (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              >
                <Card variant="default" className="h-full overflow-hidden">
                  {/* Decorative accent */}
                  <div className="h-1 bg-gradient-to-r from-purple-500 to-cyan-500" />
                  <CardContent className="p-4 sm:p-5 md:p-6 text-center h-full flex flex-col justify-center space-y-3 relative">
                    {/* Background decoration */}
                    <motion.div
                      className="absolute -right-4 -bottom-4 w-12 h-12 rounded-full bg-cyan-500/10"
                      animate={pulseAnimation}
                    />
                    <Icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto text-neutral-600 dark:text-neutral-400" />
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold">{value.title}</h3>
                    <p className="text-xs sm:text-sm md:text-base text-neutral-600 dark:text-neutral-400">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* Leadership Section */}
      <div className="px-4 sm:px-6 relative">
        <motion.div
          style={{ opacity: leadershipDecorOpacity }}
        >
          <motion.img
            src={line24}
            alt=""
            className="absolute top-10 right-10 w-7 h-7 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={floatingAnimation}
            transition={{ duration: 0.8, delay: 0.3, opacity: 0.3, scale: 1 }}
          />

          <motion.img
            src={ellipse33}
            alt=""
            className="absolute bottom-32 left-10 w-8 h-8 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={pulseAnimation}
            transition={{ duration: 0.8, delay: 0.4, opacity: 0.2, scale: 1 }}
          />
        </motion.div>

        {/* Background elements */}
        <motion.div
          className="absolute top-20 left-0 w-52 h-52 rounded-full bg-gradient-to-tr from-purple-500/5 to-transparent -z-10"
          style={{
            opacity: useTransform(scrollYProgress, [0.6, 0.7, 0.8], [0, 0.5, 0]),
            scale: useTransform(scrollYProgress, [0.6, 0.8], [0.8, 1.2])
          }}
        />

        <motion.div
          className="text-center space-y-4 mb-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <motion.h2
            className="text-xl sm:text-2xl md:text-3xl font-bold"
            variants={itemVariants}
          >
            Team
          </motion.h2>
          <motion.p
            className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400"
            variants={itemVariants}
          >
            Meet the team driving our vision forward
          </motion.p>
        </motion.div>
        <div className="max-w-4xl mx-auto">
          {leadership.map((person, index) => (
            <motion.div
              key={person.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: 0.2 * index }}
            >
              <Card variant="default" className="mb-4 overflow-hidden">
                {/* Decorative accent */}
                <div className="h-1 bg-gradient-to-r from-purple-500 to-cyan-500" />
                <CardContent className="p-4 sm:p-6 md:p-8 relative">
                  {/* Background decoration */}
                  <motion.div
                    className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-gradient-to-tl from-cyan-500/5 via-purple-500/5 to-transparent -z-10"
                    animate={pulseAnimation}
                  />

                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                      <motion.div
                        className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 relative overflow-hidden rounded-full"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img
                          src="/alfredo-headshot.jpg"
                          alt="Alfredo Diaz, Founder & Managing Partner"
                          className="object-cover w-full h-full"
                        />
                      </motion.div>

                      <div className="mt-3 flex justify-center sm:justify-start space-x-3">
                        <motion.a
                          href="https://www.linkedin.com/in/alfredodiaz1/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 relative group"
                          aria-label="LinkedIn Profile"
                          whileHover={{ scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                          <Linkedin className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            LinkedIn • linkedin.com/in/alfredodiaz1
                          </span>
                        </motion.a>
                        <motion.a
                          href="http://www.yoalfredo.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 relative group"
                          aria-label="Personal Blog"
                          whileHover={{ scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                          <Newspaper className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Personal Blog • yoalfredo.com
                          </span>
                        </motion.a>
                      </div>
                    </div>

                    <div className="flex-1 text-left">
                      <h3 className="text-lg sm:text-xl font-semibold text-center sm:text-left">{person.name}</h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 text-center sm:text-left">
                        {person.role}
                      </p>
                      <AnimatePresence>
                        {person.bio.split('\n\n').map((paragraph, i) => (
                          <motion.p
                            key={i}
                            className="text-xs sm:text-sm md:text-base text-neutral-600 dark:text-neutral-400 mb-3 leading-relaxed"
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: 0.1 * i }}
                          >
                            {paragraph}
                          </motion.p>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
} 